import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SupervisorManadaPanel from './SupervisorManadaPanel.jsx';
import { ALINEA_PROFILES } from '../utils/constants.js';
import { buildManadaInsights } from '../utils/supervisorManada.js';
import './CoachManadaView.css';

const LEVEL_META = {
    green: { label: 'Estable', className: 'coach-signal-green' },
    yellow: { label: 'Atención', className: 'coach-signal-yellow' },
    red: { label: 'Prioridad', className: 'coach-signal-red' },
};

function profileEmoji(type) {
    const k = (type || '').toLowerCase();
    return ALINEA_PROFILES[k]?.emoji || '⚪';
}

function normalizeProfileKey(raw) {
    const v = (raw || '').toLowerCase();
    if (v.includes('const')) return 'constante';
    if (v.includes('impul')) return 'impulsivo';
    if (v.includes('anal')) return 'analitico';
    if (v.includes('social')) return 'social';
    return null;
}

export default function CoachManadaView({
    isAdmin,
    isSupervisor,
    guides = [],
    leaders = [],
    employees = [],
    teamData = [],
    leaderPlan = null,
    loading = false,
    selectedLeaderId,
    onSelectLeader,
    onOpenPlan,
    loadingPlan = false,
}) {
    const [showProfileGuides, setShowProfileGuides] = useState(false);

    const employeesBySupervisor = useMemo(() => {
        const map = new Map();
        for (const emp of employees) {
            const sid = emp.supervisor_id;
            if (sid == null) continue;
            if (!map.has(sid)) map.set(sid, []);
            map.get(sid).push(emp);
        }
        return map;
    }, [employees]);

    const companyInsights = useMemo(() => buildManadaInsights(employees), [employees]);

    const selectedTeam = useMemo(() => {
        if (!selectedLeaderId) return [];
        return employeesBySupervisor.get(Number(selectedLeaderId)) || [];
    }, [employeesBySupervisor, selectedLeaderId]);

    const selectedInsights = useMemo(() => buildManadaInsights(selectedTeam), [selectedTeam]);

    const leadersSorted = useMemo(
        () =>
            [...leaders].sort((a, b) => {
                const order = { red: 0, yellow: 1, green: 2 };
                return (order[a.level] ?? 9) - (order[b.level] ?? 9);
            }),
        [leaders]
    );

    const attentionCount = leaders.filter((l) => l.level === 'red' || l.level === 'yellow').length;

    const guideByProfile = useMemo(() => {
        const m = {};
        for (const g of guides) {
            m[(g.observed_profile || '').toLowerCase()] = g;
        }
        return m;
    }, [guides]);

    const conversationList = useMemo(() => {
        const list = teamData.length ? teamData : selectedTeam;
        return list
            .map((emp) => {
                const key = normalizeProfileKey(emp.profile_type || emp.alinea_profile);
                const guide = key ? guideByProfile[key] : null;
                const questions = guide?.powerful_questions || [];
                return {
                    id: emp.id,
                    name: emp.full_name || emp.nombre,
                    area: emp.area_name || emp.area,
                    profileKey: key,
                    profileLabel: key ? ALINEA_PROFILES[key]?.name : 'Sin perfil',
                    question: questions[0] || '¿Cómo estás hoy realmente en el turno?',
                    approach: guide?.approach || guide?.description,
                };
            })
            .slice(0, 12);
    }, [teamData, selectedTeam, guideByProfile]);

    if (loading) {
        return <p className="text-muted coach-manada-loading">Cargando señales de la manada y líderes…</p>;
    }

    if (isAdmin) {
        return (
            <div className="coach-manada-view">
                <header className="coach-manada-hero glass-card">
                    <div>
                        <h3 style={{ marginTop: 0 }}>Gestión de líderes y manadas</h3>
                        <p className="text-muted coach-manada-hero-lead">
                            Señales del <strong>Observador</strong> por encargado: quién necesita acompañamiento,
                            cómo está compuesta su manada y qué conversar esta semana.
                        </p>
                    </div>
                    <div className="coach-manada-kpis">
                        <div className="coach-manada-kpi">
                            <span className="coach-manada-kpi-n">{leaders.length}</span>
                            <span className="coach-manada-kpi-l">Líderes activos</span>
                        </div>
                        <div className="coach-manada-kpi">
                            <span className={`coach-manada-kpi-n ${attentionCount ? 'warn' : ''}`}>{attentionCount}</span>
                            <span className="coach-manada-kpi-l">En atención o prioridad</span>
                        </div>
                        <div className="coach-manada-kpi">
                            <span className="coach-manada-kpi-n">{employees.length}</span>
                            <span className="coach-manada-kpi-l">Colaboradores en planta</span>
                        </div>
                    </div>
                </header>

                {companyInsights.total > 0 && (
                    <div className="glass-card coach-manada-mix">
                        <h4 style={{ marginTop: 0 }}>Mix de perfiles en la empresa</h4>
                        <div className="coach-manada-mix-chips">
                            {companyInsights.archetypeBars.map((bar) => (
                                <span key={bar.key} className="coach-manada-mix-chip" title={bar.name}>
                                    {bar.emoji} {bar.name}: {bar.count} ({bar.pct}%)
                                </span>
                            ))}
                            {companyInsights.profiles.sin_perfil > 0 && (
                                <span className="coach-manada-mix-chip muted">
                                    Sin perfil: {companyInsights.profiles.sin_perfil}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                <div className="glass-card coach-manada-leaders-card">
                    <h4 style={{ marginTop: 0 }}>Encargados y supervisores</h4>
                    <p className="text-sm text-muted mb-md">
                        Ordenados por urgencia según el Observador. Selecciona uno para ver su manada y acciones.
                    </p>
                    {leadersSorted.length === 0 ? (
                        <p className="text-muted text-sm">
                            No hay supervisores registrados. Configura líderes en{' '}
                            <Link to="/team">Base de personal</Link>.
                        </p>
                    ) : (
                        <div className="coach-manada-leaders-table-wrap">
                            <table className="coach-manada-leaders-table">
                                <thead>
                                    <tr>
                                        <th>Líder</th>
                                        <th>Manada</th>
                                        <th>Señal</th>
                                        <th>Riesgo</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leadersSorted.map((l) => {
                                        const meta = LEVEL_META[l.level] || LEVEL_META.green;
                                        const selected = Number(selectedLeaderId) === Number(l.supervisorId);
                                        const team = employeesBySupervisor.get(l.supervisorId) || [];
                                        return (
                                            <tr
                                                key={l.supervisorId}
                                                className={selected ? 'selected' : ''}
                                                onClick={() => onSelectLeader?.(l.supervisorId)}
                                            >
                                                <td className="font-bold">{l.supervisorName}</td>
                                                <td>{l.totalTeam ?? team.length} personas</td>
                                                <td>
                                                    <span className={`coach-signal-pill ${meta.className}`}>
                                                        {meta.label}
                                                    </span>
                                                </td>
                                                <td>{l.avgRisk != null ? Number(l.avgRisk).toFixed(2) : '—'}</td>
                                                <td className="coach-manada-actions-cell">
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenPlan?.(l.supervisorId);
                                                        }}
                                                    >
                                                        Plan
                                                    </button>
                                                    <Link
                                                        to={`/team?supervisorId=${l.supervisorId}`}
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        Equipo
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {selectedLeaderId && (
                    <div className="coach-manada-detail glass-card">
                        <h4 style={{ marginTop: 0 }}>
                            Detalle: {leaders.find((l) => l.supervisorId === Number(selectedLeaderId))?.supervisorName}
                        </h4>
                        {loadingPlan ? (
                            <p className="text-muted text-sm">Cargando plan del líder…</p>
                        ) : leaderPlan ? (
                            <div className="coach-manada-detail-plan">
                                <p className="text-sm">
                                    <strong>Acción sugerida (Observador):</strong> {leaderPlan.leaderGuide?.action}
                                </p>
                                <p className="text-sm">
                                    <strong>Pregunta para el líder:</strong> {leaderPlan.leaderGuide?.question}
                                </p>
                                {(leaderPlan.priorities || []).length > 0 && (
                                    <div className="mt-md">
                                        <p className="text-xs text-muted text-uppercase">Conversar primero con</p>
                                        <ul className="coach-list text-sm">
                                            {leaderPlan.priorities.slice(0, 4).map((p) => (
                                                <li key={p.workerId}>
                                                    {p.name} — {p.suggestedAction}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm mt-md"
                                    onClick={() => onOpenPlan?.(selectedLeaderId)}
                                >
                                    Abrir plan completo de mejoramiento
                                </button>
                            </div>
                        ) : null}

                        {selectedInsights.total > 0 && (
                            <div className="coach-manada-detail-team mt-lg">
                                <p className="text-sm text-muted mb-sm">
                                    Composición de su manada ({selectedInsights.total} personas)
                                </p>
                                <div className="coach-manada-mix-chips">
                                    {selectedInsights.archetypeBars.map((bar) =>
                                        bar.count > 0 ? (
                                            <span key={bar.key} className="coach-manada-mix-chip">
                                                {bar.emoji} {bar.count}
                                            </span>
                                        ) : null
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <ProfileGuidesAccordion
                    guides={guides}
                    open={showProfileGuides}
                    onToggle={() => setShowProfileGuides((v) => !v)}
                />
            </div>
        );
    }

    /* Supervisor */
    return (
        <div className="coach-manada-view">
            <header className="coach-manada-hero glass-card">
                <h3 style={{ marginTop: 0 }}>Tu manada y conversaciones</h3>
                <p className="text-muted coach-manada-hero-lead">
                    Usa el perfil ALINEA de cada persona para elegir <strong>cómo</strong> abrir la conversación, no solo
                    <strong> qué</strong> exigir. Las señales vienen del Observador y las encuestas de tu equipo.
                </p>
                <div className="coach-manada-quick-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => onOpenPlan?.()}>
                        📋 Mi plan de mejoramiento
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => onOpenPlan?.('questions')}>
                        ❓ Banco de preguntas
                    </button>
                </div>
            </header>

            <div className="glass-card coach-manada-panel-wrap">
                <h4 style={{ marginTop: 0 }}>🐺 Composición de tu manada</h4>
                <SupervisorManadaPanel teamData={teamData} />
            </div>

            {conversationList.length > 0 && (
                <div className="glass-card coach-manada-conversations">
                    <h4 style={{ marginTop: 0 }}>💬 A quién conversar esta semana</h4>
                    <p className="text-sm text-muted mb-md">
                        Sugerencia operativa por perfil. No es evaluación de desempeño.
                    </p>
                    <ul className="coach-manada-conv-list">
                        {conversationList.map((row) => (
                            <li key={row.id} className="coach-manada-conv-item">
                                <div className="coach-manada-conv-head">
                                    <span className="font-bold">{row.name}</span>
                                    <span className="coach-manada-conv-profile">
                                        {profileEmoji(row.profileKey)} {row.profileLabel}
                                    </span>
                                </div>
                                {row.approach && (
                                    <p className="text-xs text-muted">Enfoque: {row.approach}</p>
                                )}
                                <p className="coach-manada-conv-q">"{row.question}"</p>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <ProfileGuidesAccordion
                guides={guides}
                open={showProfileGuides}
                onToggle={() => setShowProfileGuides((v) => !v)}
            />
        </div>
    );
}

function ProfileGuidesAccordion({ guides, open, onToggle }) {
    return (
        <div className="glass-card coach-manada-guides-acc">
            <button type="button" className="coach-manada-acc-toggle" onClick={onToggle} aria-expanded={open}>
                {open ? '▾' : '▸'} Referencia: guías por perfil ALINEA ({guides.length})
            </button>
            {open && (
                <div className="grid-2 coach-manada-guides-grid">
                    {guides.map((guide, i) => (
                        <div key={i} className="coach-manada-guide-card">
                            <h5 style={{ margin: '0 0 0.5rem' }}>
                                {profileEmoji(guide.observed_profile)} {guide.observed_profile}
                            </h5>
                            <p className="text-sm text-muted">
                                <strong>Enfoque:</strong> {guide.approach || guide.description}
                            </p>
                            <ul className="coach-list text-sm">
                                {(guide.powerful_questions || []).slice(0, 4).map((q, j) => (
                                    <li key={j}>"{q}"</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
